/**
 * @file Category-only operator wording for amended DVA IHI revalidation search outcomes.
 * @module pages/admin/hi-alerts/amendedSearchOutcome
 *
 * B1.3H-3R-11 — surfaces the category-only `outcome` and `assignment_outcome`
 * labels that the authentication amended-search endpoint already returns
 * (confirmed by the B1.3H-3R-10 source inspection) as neutral operator wording.
 *
 * Requirement distinction (kept deliberately separate):
 *  - UI/action requirement `HI_015_005818` — amended-search resubmission after a
 *    no-match. This module describes the RESULT of that operator action.
 *  - Hosting review row `HI_015_006105` — the DVA IHI revalidation no-match review
 *    the amended search is launched from. That review is the row that stays open;
 *    it is not what this wording describes.
 *
 * Privacy / scope: these are bounded category labels only. This module never
 * receives or produces a raw/masked IHI, DVA file number, Medicare number, IRN,
 * demographics, a match count, or any request/response body. It only maps a
 * category token to fixed, human-readable wording.
 *
 * It deliberately does NOT imply that an IHI was assigned. The only outcome where
 * the backend touched the current IHI is `assignable_single_match` +
 * `reaffirmed_current_ihi`, which reaffirms the status of the IHI already on
 * record — not a new assignment — and the wording says exactly that. Every other
 * outcome states plainly that no IHI was applied.
 *
 * Unknown / unrecognised labels fall back to a neutral safe message instead of
 * throwing, so a future backend label can never crash the surface.
 */

/** Classifier `outcome` values returned by the amended-search endpoint. */
export const OUTCOME_ASSIGNABLE_SINGLE_MATCH = "assignable_single_match";
export const OUTCOME_UNVERIFIED_SINGLE_MATCH = "unverified_single_match";
export const OUTCOME_UNSUPPORTED_SINGLE_MATCH = "unsupported_single_match";
export const OUTCOME_NO_MATCH = "no_match";
export const OUTCOME_MULTIPLE_MATCH = "multiple_match";
export const OUTCOME_INDETERMINATE = "indeterminate";

/**
 * `assignment_outcome` values. The backend returns one of these ONLY when the
 * outcome is `assignable_single_match`; for every other outcome it is null.
 */
export const ASSIGNMENT_REAFFIRMED_CURRENT_IHI = "reaffirmed_current_ihi";
export const ASSIGNMENT_BLOCKED_DIFFERENT_IHI = "blocked_different_ihi";
export const ASSIGNMENT_SKIPPED_NO_CURRENT_IHI = "skipped_no_current_ihi";
export const ASSIGNMENT_SKIPPED_NO_CURRENT_DVA = "skipped_no_current_dva";
export const ASSIGNMENT_SKIPPED_PATIENT_MISSING = "skipped_patient_missing";
export const ASSIGNMENT_SKIPPED_PATIENT_INFO_MISSING = "skipped_patient_info_missing";
export const ASSIGNMENT_SKIPPED_PATIENT_INACTIVE = "skipped_patient_inactive";
export const ASSIGNMENT_SKIPPED_NO_RETURNED_IHI = "skipped_no_returned_ihi";

/**
 * The persistent operator reminder. The amended search never resolves, supersedes
 * or acknowledges the originating no-match review — the operator still resolves it
 * explicitly — so this note is shown alongside every amended-search result.
 */
export const REVIEW_REMAINS_OPEN_NOTE =
	"The original no-match review remains open for explicit operator resolution.";

/** Neutral, category-only description of an amended-search result. */
export interface AmendedSearchOutcomeMessage {
	/**
	 * Alert tone: `info` for neutral / no-issue outcomes, `warning` where the
	 * operator should note that a match was involved but no IHI was applied.
	 */
	severity: "info" | "warning";
	title: string;
	detail: string;
}

/**
 * Map the category-only `outcome` / `assignment_outcome` pair to neutral operator
 * wording. `assignment_outcome` is only meaningful under
 * `assignable_single_match`; for every other outcome the backend sends null and it
 * is ignored here. Any unrecognised value falls back to a safe neutral message.
 */
export const describeAmendedSearchOutcome = (
	outcome: string | null | undefined,
	assignmentOutcome: string | null | undefined
): AmendedSearchOutcomeMessage => {
	switch (outcome) {
		case OUTCOME_NO_MATCH:
			return {
				severity: "info",
				title: "No match found",
				detail: "The amended search returned no match. No IHI was applied.",
			};
		case OUTCOME_MULTIPLE_MATCH:
			return {
				severity: "warning",
				title: "Multiple matches found",
				detail:
					"More than one possible match was returned, so no single record could be selected. No IHI was applied.",
			};
		case OUTCOME_UNVERIFIED_SINGLE_MATCH:
			return {
				severity: "warning",
				title: "Single match found — no IHI applied",
				detail:
					"A single matching record was found, but its verification status was unverified, so no IHI was applied.",
			};
		case OUTCOME_UNSUPPORTED_SINGLE_MATCH:
			return {
				severity: "warning",
				title: "Single match found — no IHI applied",
				detail:
					"A single matching record was found, but its status is not supported for assignment, so no IHI was applied.",
			};
		case OUTCOME_ASSIGNABLE_SINGLE_MATCH:
			return describeAssignableSingleMatch(assignmentOutcome);
		case OUTCOME_INDETERMINATE:
			return {
				severity: "warning",
				title: "Result could not be determined",
				detail: "The amended search result could not be determined. No IHI was applied.",
			};
		default:
			// Unknown / future outcome label: never crash, never imply an assignment.
			return {
				severity: "info",
				title: "Amended search submitted",
				detail: "The amended search was submitted. No IHI was applied automatically.",
			};
	}
};

/**
 * Describe the `assignable_single_match` sub-outcomes. Only
 * `reaffirmed_current_ihi` touched the IHI on record (a reaffirmation of the
 * existing value — not a new assignment); every other value means no IHI was
 * applied. An unknown/absent assignment label falls back to a safe neutral
 * single-match message that never implies an assignment occurred.
 */
const describeAssignableSingleMatch = (
	assignmentOutcome: string | null | undefined
): AmendedSearchOutcomeMessage => {
	switch (assignmentOutcome) {
		case ASSIGNMENT_REAFFIRMED_CURRENT_IHI:
			return {
				severity: "info",
				title: "Single match found",
				detail:
					"A single matching record was found and the IHI already on record was reaffirmed. No new IHI was assigned.",
			};
		case ASSIGNMENT_BLOCKED_DIFFERENT_IHI:
			return {
				severity: "warning",
				title: "Single match found — no IHI applied",
				detail:
					"A single matching record was found, but its IHI differs from the IHI already on record. No IHI was applied and a separate review was opened for operator attention.",
			};
		case ASSIGNMENT_SKIPPED_NO_CURRENT_IHI:
			return {
				severity: "warning",
				title: "Single match found — no IHI applied",
				detail:
					"A single matching record was found, but no IHI was applied because there is no current IHI on record for this patient.",
			};
		case ASSIGNMENT_SKIPPED_NO_CURRENT_DVA:
			return {
				severity: "warning",
				title: "Single match found — no IHI applied",
				detail:
					"A single matching record was found, but no IHI was applied because there is no current DVA file number on record for this patient.",
			};
		case ASSIGNMENT_SKIPPED_PATIENT_INACTIVE:
			return {
				severity: "warning",
				title: "Single match found — no IHI applied",
				detail:
					"A single matching record was found, but no IHI was applied because this patient record is not active.",
			};
		case ASSIGNMENT_SKIPPED_PATIENT_MISSING:
		case ASSIGNMENT_SKIPPED_PATIENT_INFO_MISSING:
			return {
				severity: "warning",
				title: "Single match found — no IHI applied",
				detail:
					"A single matching record was found, but no IHI was applied because the patient record is no longer available.",
			};
		case ASSIGNMENT_SKIPPED_NO_RETURNED_IHI:
			return {
				severity: "warning",
				title: "Single match found — no IHI applied",
				detail:
					"A single matching record was found, but no usable IHI was returned, so no IHI was applied.",
			};
		default:
			// Assignable single match with an unknown/absent assignment label: safe,
			// non-committal wording that never implies an assignment occurred.
			return {
				severity: "warning",
				title: "Single match found — no IHI applied",
				detail: "A single matching record was found, but no IHI was applied.",
			};
	}
};

/** CU-14ygtkj98pb: the bounded single-match labels a UC.015 Medicare/address amended search can return. */
export const OUTCOME_SINGLE_MATCH = "single_match";
export const RELATION_SAME_CURRENT_IHI = "same_current_ihi";
export const RELATION_DIFFERENT_CURRENT_IHI = "different_current_ihi";
export const TRANSITION_NO_CHANGE = "no_change";
export const TRANSITION_WARNING_OPTIONAL_NO_ACTION = "warning_optional_no_action";
export const TRANSITION_STORED_RETIRED = "stored_retired_warning_required_later";
export const TRANSITION_ALERT_REQUIRED = "alert_required_later";

/**
 * CU-14ygtkj98pb (HI_015_005818): no-match, multiple-match and indeterminate reuse the wording above; a single match is
 * described only by its bounded relation and transition labels, never implying a new IHI was assigned.
 */
export const describeRevalidationAmendedSearchOutcome = (
	outcome: string | null | undefined,
	relation: string | null | undefined,
	transitionAction: string | null | undefined
): AmendedSearchOutcomeMessage => {
	if (outcome !== OUTCOME_SINGLE_MATCH) {
		return describeAmendedSearchOutcome(outcome, null);
	}
	if (transitionAction === TRANSITION_ALERT_REQUIRED) {
		return {
			severity: "warning",
			title: "Single match found — alert raised",
			detail: "A single matching record was found with a status that needs operator attention. No new IHI was applied and an alert was raised in HI Reviews.",
		};
	}
	if (relation === RELATION_SAME_CURRENT_IHI && transitionAction === TRANSITION_STORED_RETIRED) {
		return {
			severity: "warning",
			title: "Single match found — IHI status updated",
			detail: "The amended search returned the IHI already on record with a Retired status. Its status was updated to Retired and a warning was raised in HI Reviews. No new IHI was assigned.",
		};
	}
	if (relation === RELATION_SAME_CURRENT_IHI && (transitionAction === TRANSITION_NO_CHANGE || transitionAction === TRANSITION_WARNING_OPTIONAL_NO_ACTION)) {
		return {
			severity: "info",
			title: "Single match found",
			detail: "The amended search returned the IHI already on record. The IHI and its statuses were not changed.",
		};
	}
	if (relation === RELATION_DIFFERENT_CURRENT_IHI) {
		return {
			severity: "warning",
			title: "Single match found — no IHI applied",
			detail: "A single matching record was found, but its IHI differs from the IHI already on record. No IHI was applied.",
		};
	}
	return {
		severity: "warning",
		title: "Single match found — no IHI applied",
		detail: "A single matching record was found, but no change was made to the IHI on record.",
	};
};
